import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import { LectureDto } from "../../interfaces";
import { useTranslation } from "react-i18next";
import { NavLink, useParams } from "react-router-dom";

import { TableSpinner } from "../../components";
import { MediaStatus, NoResultFound } from "../../components/Common";
import DeleteModal from "../../components/Common/DeleteModal";
import { AddEditLectureModal } from "./AddEditLectureModal";
import {
  useGetLecturesByLessonQuery,
  useDeleteLectureMutation,
  useToggleLectureStateMutation,
} from "../../store/api/lectureApi";
import noThumbnail from "../../assets/images/no-thumbnail.png";
import { RoleEnum } from "../../enums";
import { MediaStatusEnum } from "../../interfaces/media";
import { AccountState } from "../../store/slices/accountSlice";
import { useGetLessonQuery } from "../../store/api/lessonApi";

export const LecturesPage = () => {
  //#region hooks
  const { t, i18n } = useTranslation();
  const { lessonId } = useParams<any>();
  const { subjectId } = useParams<any>();
  //#endregion

  //#region internal state
  const [showLectureDeleteModal, setShowLectureDeleteModal] =
    useState<boolean>(false);
  const [deletingLectureId, setDeletingLectureId] = useState<string | null>(
    null,
  );
  const [showAddEditLectureModal, setShowAddEditLectureModal] =
    useState<boolean>(false);
  const [editingLectureId, setEditingLectureId] = useState<string | null>(null);
  const [conversionPollingInterval, setConversionPollingInterval] =
    useState(0);
  //#endregion

  //#region api
  const {
    data: dataLectures,
    isLoading: isLoadingLectures,
    refetch,
  } = useGetLecturesByLessonQuery(lessonId, {
    skip: !lessonId,
    pollingInterval: conversionPollingInterval,
  });

  const { data: dataLesson } = useGetLessonQuery(lessonId, { skip: !lessonId });

  const [
    deleteLecture,
    { isLoading: deleteLectureLoading, error: deleteLectureErrorRaw },
  ] = useDeleteLectureMutation();

  const [toggleLectureState] = useToggleLectureStateMutation();

  const deleteLectureError = deleteLectureErrorRaw
    ? (deleteLectureErrorRaw as any)?.data?.message || "An error occurred"
    : undefined;

  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  //#endregion

  //#region handlers

  const onEdit = (id: string) => {
    setEditingLectureId(id);
    setShowAddEditLectureModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingLectureId(id);
    setShowLectureDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteLecture(deletingLectureId as string).unwrap();
      setDeletingLectureId(null);
      setShowLectureDeleteModal(false);
    } catch (err) {}
  };

  const onAdd = () => {
    setEditingLectureId(null);
    setShowAddEditLectureModal(true);
  };

  const onToggleState = (item: LectureDto) => {
    toggleLectureState(item.id);
  };

  useEffect(() => {
    const shouldPollConversion = Boolean(
      dataLectures?.some(
        (item) =>
          item.video?.id &&
          (item.video.status === MediaStatusEnum.PROCESSING ||
            item.video.status === MediaStatusEnum.NEW ||
            !item.video.status),
      ),
    );
    setConversionPollingInterval(shouldPollConversion ? 15000 : 0);
  }, [dataLectures]);

  //#endregion

  return (
    <Container fluid>
      <Row className="mt-2 mb-4 align-items-center">
        <Col className="col-sm-auto">
          <div className="d-flex align-items-center flex-nowrap">
            <h4>
              <NavLink to={"statistics"} className="cursor-pointer">
                {t("lectures")}
              </NavLink>
            </h4>
            {i18n.language === "ar" ? (
              <i className="ri-arrow-left-s-line align-bottom text-muted"></i>
            ) : (
              <i className="ri-arrow-right-s-line align-bottom text-muted"></i>
            )}
            <h4 className="text-muted">
              {i18n.language === "ar"
                ? dataLesson?.name.ar
                : dataLesson?.name.en}
            </h4>
          </div>
        </Col>
        <Col className="col-sm">
          <div className="d-flex justify-content-sm-end gap-2">
            <Button color="success invisible">
              <i className="ri-add-fill me-1 align-bottom"></i>
              Added Me for better Alignment
            </Button>
          </div>
        </Col>
      </Row>
      <React.Fragment>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              <Button
                color="success"
                onClick={() => {
                  refetch();
                }}
              >
                {t("refresh")}
              </Button>
              <Button color="success" onClick={() => onAdd()}>
                <i className="ri-add-fill me-1 align-bottom"></i>
                {t("addItem", { item: "lecture" })}
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="mt-3">
          {isLoadingLectures && <TableSpinner />}
          {!isLoadingLectures &&
            (!dataLectures || dataLectures.length == 0) && <NoResultFound />}
          {!isLoadingLectures && dataLectures && dataLectures.length > 0 && (
            <React.Fragment>
              <Col lg={12}>
                <Row className="team-list grid-view-filter">
                  {dataLectures.map((item: LectureDto, key: number) => (
                    <Col key={key} xs={12} sm={6} lg={4} xl={3} xxl={3}>
                      <Card className="team-box cursor-pointer">
                        <div className="item-summary-custom d-flex flex-wrap align-items-center gap-1 m-2">
                          <Badge
                            color={`${item.isActive ? "primary" : "danger"}`}
                          >
                            {item.isActive ? t("active") : t("inActive")}
                          </Badge>
                          <Badge color="secondary">
                            {t("sequenceNumber")}: {item.sequenceNumber}
                          </Badge>
                          <Badge color="primary">
                            {t("assignment")}: {item.totalAssignments}
                          </Badge>
                          <Badge color="primary">
                            {t("questions")}: {item.totalQuestions}
                          </Badge>
                          {item.video?.id && (
                            <MediaStatus
                              status={
                                item?.video?.status || MediaStatusEnum.NEW
                              }
                              mediaId={item.video?.id}
                              type={"Lecture"}
                              lectureOrSkillId={item.id}
                            />
                          )}
                        </div>
                        {item?.video?.thumbnail ? (
                          <img
                            onClick={() => onEdit(item.id)}
                            src={item?.video?.thumbnail}
                            alt="thumbnail"
                            className="img-fluid"
                          />
                        ) : (
                          <img
                            onClick={() => onEdit(item.id)}
                            src={noThumbnail}
                            alt="thumbnail"
                            className="img-fluid"
                          />
                        )}
                        <CardBody>
                          <UncontrolledDropdown
                            style={
                              i18n.language === "ar"
                                ? { position: "absolute", top: 10, left: 15 }
                                : { position: "absolute", top: 10, right: 15 }
                            }
                          >
                            <DropdownToggle
                              tag="a"
                              id="dropdownMenuLink2"
                              role="button"
                            >
                              <i className="ri-more-fill fs-17 dropdown-menu-button-custom"></i>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem
                                style={{
                                  alignSelf: "flex-start",
                                  display: "flex",
                                }}
                                className="dropdown-item edit-list"
                                onClick={() => onEdit(item.id)}
                              >
                                <i className="ri-pencil-line me-2 align-bottom text-muted"></i>
                                {t("edit")}
                              </DropdownItem>
                              {profile &&
                                profile.role.key == RoleEnum.SUPER_ADMIN && (
                                  <DropdownItem
                                    style={{
                                      alignSelf: "flex-start",
                                      display: "flex",
                                    }}
                                    className="dropdown-item edit-list"
                                    onClick={() => onToggleState(item)}
                                  >
                                    <i
                                      className={`${
                                        item.isActive
                                          ? "ri-forbid-line"
                                          : "ri-checkbox-circle-line text-danger"
                                      }  me-2 align-bottom text-muted`}
                                    ></i>
                                    {item.isActive
                                      ? t("deactivate")
                                      : t("activate")}
                                  </DropdownItem>
                                )}
                              <DropdownItem
                                style={{
                                  alignSelf: "flex-start",
                                  display: "flex",
                                }}
                                className="dropdown-item remove-list"
                                onClick={() => onDelete(item.id)}
                              >
                                <i className="ri-delete-bin-5-line me-2 align-bottom text-muted"></i>
                                {t("delete")}
                              </DropdownItem>
                              <DropdownItem
                                tag={NavLink}
                                style={{
                                  alignSelf: "flex-start",
                                  display: "flex",
                                }}
                                className="dropdown-item edit-list"
                                to={`${lessonId}/${item.id}/assignments`}
                              >
                                <i className="ri-pencil-line me-2 align-bottom text-muted"></i>
                                {t("manageAssignments")}
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                          <Row onClick={() => onEdit(item.id)}>
                            <Col sm={12}>
                              <h5 className="mb-1">
                                {i18n.language === "ar"
                                  ? item.name.ar
                                  : item.name.en}
                              </h5>
                              {(item.author?.value?.ar ||
                                item.author?.value?.en) && (
                                <p className="text-muted mb-0 small">
                                  {t("author")}:{" "}
                                  {i18n.language === "ar"
                                    ? item.author.value.ar ||
                                      item.author.value.en
                                    : item.author.value.en ||
                                      item.author.value.ar}
                                </p>
                              )}
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
            </React.Fragment>
          )}
        </Row>
        <DeleteModal
          loading={deleteLectureLoading}
          show={showLectureDeleteModal}
          onDeleteClick={() => onDeleteConfirm()}
          onCloseClick={() => {
            setShowLectureDeleteModal(false);
          }}
          error={deleteLectureError}
        />
        <AddEditLectureModal
          lessonName={""}
          subjectId={subjectId}
          lessonId={lessonId}
          id={editingLectureId}
          show={showAddEditLectureModal}
          onCloseClick={() => {
            setShowAddEditLectureModal(false);
            setEditingLectureId(null);
          }}
        />
      </React.Fragment>
    </Container>
  );
};
